import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import SplitText from "@/components/SplitText";

// Reusable kinetic hero: parallax background + masked line-by-line headline reveal.
export default function KineticHero({ eyebrow, lines, subtitle, image }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "32%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative min-h-[82vh] md:min-h-screen flex items-end overflow-hidden hero-spotlight"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src={image} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-gl-bg via-gl-bg/60 to-gl-bg/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-gl-bg/80 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gl-primary/15 mix-blend-multiply" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 1 }}
        className="absolute top-28 md:top-32 left-0 right-0"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center gap-4">
          <motion.span
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="h-px w-16 bg-gl-gold origin-left"
          />
          <span className="text-gl-gold uppercase tracking-[0.35em] text-[11px] md:text-xs">
            {eyebrow}
          </span>
        </div>
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity }}
        className="relative max-w-7xl mx-auto px-6 md:px-12 w-full pb-20 md:pb-28"
      >
        <SplitText
          as="h1"
          className="font-serif-display font-light leading-[0.96] text-gl-text text-5xl sm:text-6xl lg:text-8xl"
          lines={lines}
          delay={0.45}
          stagger={0.12}
        />
        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9 }}
            className="mt-8 text-lg md:text-xl text-gl-text2 font-light leading-relaxed max-w-2xl"
          >
            {subtitle}
          </motion.p>
        )}
      </motion.div>
    </section>
  );
}
